import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Asterisk } from '@/components/generals/asterisk'
import { marqueeItems } from '@/constants/content'
import { siteConfig } from '@/constants/site'

type MarqueeTone = 'display' | 'label'

interface MarqueeRowProps {
    items: string[]
    tone: MarqueeTone
    reverse?: boolean
}

const MarqueeRow: FC<MarqueeRowProps> = ({ items, tone, reverse = false }): ReactNode => {
    const textClassName = (index: number): string => {
        if (tone === 'label') return 'font-mono font-medium uppercase tracking-[0.16em]'
        return index % 2 === 1 ? 'font-serif font-normal italic' : 'font-heading font-semibold tracking-[-0.03em]'
    }

    return (
        <div className='marquee overflow-hidden'>
            <div className={cn('flex w-max', reverse ? 'marquee-track-reverse' : 'marquee-track')}>
                {[0, 1].map(copy => (
                    <ul key={copy} aria-hidden={copy === 1 ? true : undefined} className='flex shrink-0 items-center'>
                        {items.map((item, index) => (
                            <li
                                key={`${item}-${index}`}
                                className={cn(
                                    'flex shrink-0 items-center whitespace-nowrap',
                                    tone === 'display' ? 'gap-6 pr-6 text-4xl md:gap-10 md:pr-10 md:text-7xl' : 'gap-5 pr-5 text-xs md:gap-6 md:pr-6 md:text-sm'
                                )}
                            >
                                <span className={textClassName(index)}>
                                    {item}
                                </span>

                                <Asterisk className='size-[0.6em] shrink-0' />
                            </li>
                        ))}
                    </ul>
                ))}
            </div>
        </div>
    )
}

const availabilityItems = [
    "Available for new projects",
    siteConfig.availability,
    "Based in Lisbon",
    "Working worldwide"
]

export const Marquee: FC = (): ReactNode => {
    return (
        <section aria-label="Disciplines" className='relative overflow-hidden py-16 md:py-24'>
            <div className='-mx-[5vw] rotate-[2.5deg] bg-foreground py-3 text-background md:py-4'>
                <MarqueeRow
                    items={[...availabilityItems, ...availabilityItems, ...availabilityItems]}
                    tone='label'
                    reverse
                />
            </div>

            <div className='relative -mx-[5vw] -mt-8 -rotate-2 bg-brand py-4 text-brand-foreground shadow-[0_30px_60px_-30px_rgba(43,53,255,0.7)] md:-mt-12 md:py-6'>
                <MarqueeRow items={marqueeItems} tone='display' />
            </div>
        </section>
    )
}
